import { useState } from 'react';
import { ImagePlus, RotateCcw } from 'lucide-react';
import { useSiteConfig, saveSiteConfig, fileToDataUrl } from '../../../utils/siteConfig';
import { HERO_IMAGE } from '../../../data/catalog';
import { useQueue } from '../../../context/QueueContext';
import DestinationPicker from './DestinationPicker';

const inputCls =
  'mt-1 h-12 w-full rounded-xl border border-input bg-white px-4 text-sm outline-none focus:border-navy focus:ring-2 focus:ring-navy/15';
const labelCls = 'block text-xs font-semibold uppercase tracking-wide text-muted-foreground';

export default function HomeContentSection({ flash }) {
  const { user } = useQueue();
  const cfg = useSiteConfig();
  const [form, setForm] = useState({ ...cfg.home });

  const uploadHero = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const dataUrl = await fileToDataUrl(file, 1600);
    setForm((f) => ({ ...f, [field]: dataUrl }));
    e.target.value = '';
  };

  const save = async () => {
    try {
      await saveSiteConfig({ home: { ...form } }, user, 'Alterou o conteúdo principal da Home.');
      flash('Conteúdo da Home salvo.');
    } catch (e) {
      flash(e.message || 'Falha ao salvar o conteúdo da Home.');
    }
  };

  return (
    <section className="rounded-2xl border border-navy/10 bg-white p-5" data-testid="pers-home-section">
      <h3 className="font-serif text-xl text-navy">Home — Conteúdo principal</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Estes textos aparecem quando não há banners ativos. Com banners ativos, o carrossel assume o topo da Home.
      </p>

      <div className="mt-4 space-y-4">
        <div>
          <label htmlFor="pers-home-title" className={labelCls}>Título principal</label>
          <textarea id="pers-home-title" data-testid="pers-home-title" value={form.heroTitle} onChange={(e) => setForm({ ...form, heroTitle: e.target.value })} rows={2} className="mt-1 w-full rounded-xl border border-input bg-white px-4 py-3 text-sm outline-none focus:border-navy" />
        </div>
        <div>
          <label htmlFor="pers-home-text" className={labelCls}>Texto principal</label>
          <textarea id="pers-home-text" data-testid="pers-home-text" value={form.heroText} onChange={(e) => setForm({ ...form, heroText: e.target.value })} rows={3} className="mt-1 w-full rounded-xl border border-input bg-white px-4 py-3 text-sm outline-none focus:border-navy" />
        </div>
        <div>
          <label htmlFor="pers-home-button" className={labelCls}>Texto do botão</label>
          <input id="pers-home-button" data-testid="pers-home-button" value={form.heroButtonText} onChange={(e) => setForm({ ...form, heroButtonText: e.target.value })} className={inputCls} />
        </div>
        <div>
          <p className={labelCls}>Destino do botão</p>
          <div className="mt-1">
            <DestinationPicker testid="home-dest" destType={form.heroDestType} destValue={form.heroDestValue} onChange={(t, v) => setForm({ ...form, heroDestType: t, heroDestValue: v })} />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="rounded-xl border border-navy/10 bg-white p-3">
            <p className={labelCls}>Imagem principal (desktop)</p>
            <img
              src={form.heroImageDesktop || HERO_IMAGE}
              alt="Imagem principal atual"
              className="mt-2 h-24 w-full rounded-lg object-cover"
              data-testid="pers-home-img-desktop-preview"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
            <label htmlFor="pers-home-img-desktop" data-testid="pers-home-img-desktop-label" className="mt-2 flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-navy/25 px-3 py-3 text-xs font-semibold text-navy hover:bg-navy-light">
              <ImagePlus className="h-4 w-4" /> {form.heroImageDesktop ? 'SUBSTITUIR' : 'ENVIAR IMAGEM'}
            </label>
            <input id="pers-home-img-desktop" data-testid="pers-home-img-desktop-input" type="file" accept="image/*" className="sr-only" onChange={(e) => uploadHero(e, 'heroImageDesktop')} />
          </div>
          <div className="rounded-xl border border-navy/10 bg-white p-3">
            <p className={labelCls}>Imagem mobile (opcional)</p>
            {form.heroImageMobile && <img src={form.heroImageMobile} alt="" className="mt-2 h-24 w-full rounded-lg object-cover" data-testid="pers-home-img-mobile-preview" />}
            <label htmlFor="pers-home-img-mobile" data-testid="pers-home-img-mobile-label" className="mt-2 flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-navy/25 px-3 py-3 text-xs font-semibold text-navy hover:bg-navy-light">
              <ImagePlus className="h-4 w-4" /> {form.heroImageMobile ? 'SUBSTITUIR' : 'ENVIAR IMAGEM'}
            </label>
            <input id="pers-home-img-mobile" data-testid="pers-home-img-mobile-input" type="file" accept="image/*" className="sr-only" onChange={(e) => uploadHero(e, 'heroImageMobile')} />
            {!form.heroImageMobile && <p className="mt-1 text-[11px] text-muted-foreground">Sem imagem mobile, a de desktop será usada.</p>}
          </div>
        </div>
        {(form.heroImageDesktop || form.heroImageMobile) && (
          <button
            type="button"
            onClick={() => setForm({ ...form, heroImageDesktop: null, heroImageMobile: null })}
            data-testid="pers-home-img-restore"
            className="flex items-center gap-2 rounded-full border border-red-300 px-5 py-3 text-sm font-semibold text-red-700 hover:bg-red-50"
          >
            <RotateCcw className="h-4 w-4" /> RESTAURAR IMAGEM PADRÃO
          </button>
        )}
      </div>

      <button type="button" onClick={save} data-testid="pers-home-save" className="mt-4 w-full rounded-full bg-navy px-6 py-3.5 text-sm font-semibold text-white hover:bg-navy-hover sm:w-auto">
        SALVAR ALTERAÇÕES
      </button>
    </section>
  );
}
